<template>
  <div class="auth-split-container">
    <!-- Left Sidebar: Book Quote & Cozy Illustration (Desktop Only) -->
    <div class="auth-split-sidebar">
      <img src="@/assets/img/book_auth_bg.png" alt="Lối Đọc Library" class="auth-sidebar-img" />
      <div class="auth-sidebar-overlay auth-bg-gradient"></div>
      
      <div class="auth-sidebar-content px-4 text-white">
        <router-link to="/" class="d-flex align-items-center gap-3 mb-5 text-decoration-none text-white brand-home-link" title="Trở về trang chủ">
          <div class="d-flex align-items-center justify-content-center" style="width: 44px; height: 44px;">
            <img src="@/assets/img/loidoc_logo_white.png" alt="Lối Đọc" style="width: 40px; height: 40px; object-fit: contain;" />
          </div>
          <div>
            <h1 class="h2 fw-bold m-0 font-serif" style="letter-spacing: 0.5px;">Lối Đọc</h1>
            <small class="text-white-50">Nuôi dưỡng tâm hồn qua từng trang sách</small>
          </div>
        </router-link>

        <!-- Book Quote Slider with Glassmorphic Card -->
        <div class="glass-card p-4 text-white mt-5 position-relative overflow-hidden fade-in">
          <icon icon="fa-solid fa-quote-left" class="text-white-50 fs-2 mb-3" />
          <p class="fs-5 lh-base font-serif italic mb-3">
            "{{ currentQuote.text }}"
          </p>
          <div class="text-end text-white-50 fw-semibold font-serif">
            — {{ currentQuote.author }}
          </div>
          <!-- Decorative Book Icon floating in Card background -->
          <icon icon="fa-solid fa-book" class="position-absolute text-white-50 floating-book" style="bottom: -20px; right: -20px; font-size: 8rem; opacity: 0.08;" />
        </div>
      </div>
    </div>

    <!-- Right Side: Auth Form (Desktop Split / Mobile App-like view) -->
    <div class="auth-split-main">
      <!-- App Header shown ONLY on Mobile -->
      <div class="mobile-app-header d-lg-none d-flex justify-content-between align-items-center mb-3">
        <router-link to="/" class="mobile-app-brand font-serif text-decoration-none d-flex align-items-center gap-2">
          <img src="@/assets/img/loidoc_logo_custom.png" alt="Lối Đọc" style="width: 28px; height: 28px; object-fit: contain;" />
          <span>Lối Đọc</span>
        </router-link>
        <router-link to="/" class="btn-mobile-home-icon text-decoration-none text-primary-color fs-6" title="Về trang chủ">
          <icon icon="fa-solid fa-house" />
        </router-link>
      </div>

      <!-- Main Login Card -->
      <div class="auth-form-card fade-in">
        <!-- Back to Home Button (Desktop only) -->
        <div class="mb-3 d-none d-lg-block">
          <router-link to="/" class="btn-auth-back-home" title="Trở về Trang chủ">
            <icon icon="fa-solid fa-arrow-left" class="arrow-icon" />
            <icon icon="fa-solid fa-house" class="home-icon" />
            <span>Trang chủ</span>
          </router-link>
        </div>

        <!-- iOS Segmented Control (Toggles routing to Register page) -->
        <div class="segmented-control">
          <button class="tab-link active">Đăng nhập</button>
          <router-link to="/register" class="tab-link">Đăng ký</router-link>
          <div class="slider"></div>
        </div>

        <!-- Auth Header Text -->
        <h2 class="h3 fw-bold text-primary-color font-serif mb-2">Chào mừng trở lại</h2>
        <p class="text-muted mb-4">Đăng nhập để tiếp tục hành trình đọc sách của bạn.</p>

        <!-- Login Form -->
        <div v-if="!isOtpStep">
          <form @submit.prevent="handleLogin" novalidate>
          <!-- Username Input -->
          <div class="custom-form-group">
            <label class="form-label font-serif">Tên đăng nhập</label>
            <div class="input-wrapper">
              <input 
                v-model.trim="username"
                type="text" 
                class="custom-input" 
                :class="{ 'is-invalid border-danger': errors.username }"
                placeholder="Tên đăng nhập"
                @input="clearError('username')"
              />
              <icon icon="fa-solid fa-user" class="custom-input-icon" />
            </div>
            <div v-if="errors.username" class="error-message">
              {{ errors.username }}
            </div>
          </div>

          <!-- Password Input -->
          <div class="custom-form-group">
            <label class="form-label font-serif">Mật khẩu</label>
            <div class="input-wrapper">
              <input 
                v-model="password"
                :type="showPassword ? 'text' : 'password'" 
                class="custom-input" 
                :class="{ 'is-invalid border-danger': errors.password }"
                placeholder="Mật khẩu"
                @input="clearError('password')"
              />
              <icon icon="fa-solid fa-lock" class="custom-input-icon" />
              <icon 
                :icon="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'" 
                class="custom-input-eye"
                @click="togglePassword"
              />
            </div>
            <div v-if="errors.password" class="error-message">
              {{ errors.password }}
            </div>
          </div>

          <!-- Submit Button -->
          <button 
            type="submit" 
            class="btn-book-primary" 
            :disabled="isLoading"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            <icon v-else icon="fa-solid fa-sign-in-alt" />
            <span>{{ isLoading ? 'Đang đăng nhập...' : 'Đăng nhập' }}</span>
          </button>
        </form>

        <!-- Social Divider -->
        <div class="social-divider">
          <span>hoặc tiếp tục với</span>
        </div>

        <!-- Social Login Buttons Container -->
        <div class="social-buttons-container">
          <!-- Google Login Button -->
          <button 
            type="button" 
            class="btn-social btn-social-google w-100" 
            :disabled="isGoogleLoading || isLoading"
            @click="handleGoogleLogin"
          >
            <span v-if="isGoogleLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <svg v-else class="social-icon-svg" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
            </svg>
            <span>{{ isGoogleLoading ? 'Đang kết nối...' : 'Đăng nhập với Google' }}</span>
          </button>
        </div>
      </div>

      <!-- OTP Form -->
      <div v-else>
        <div class="mb-4 text-center">
          <p class="text-muted">Chúng tôi đã gửi mã OTP gồm 6 chữ số đến email của bạn. Vui lòng kiểm tra và nhập mã để tiếp tục.</p>
        </div>
        <form @submit.prevent="handleVerifyOtp" novalidate>
          <div class="custom-form-group text-center">
            <label class="form-label font-serif d-block text-center mb-2">Mã xác thực OTP</label>
            <div class="input-wrapper">
              <input 
                v-model.trim="otpCode"
                type="text" 
                inputmode="numeric"
                autocomplete="one-time-code"
                autofocus
                class="custom-input otp-input-field text-center" 
                :class="{ 'is-invalid border-danger': errors.otp }"
                placeholder="------"
                maxlength="6"
                @input="clearError('otp')"
              />
            </div>
            <div v-if="errors.otp" class="error-message text-center mt-2">
              {{ errors.otp }}
            </div>
          </div>
          
          <button 
            type="submit" 
            class="btn-book-primary mt-4" 
            :disabled="isOtpLoading"
          >
            <span v-if="isOtpLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            <icon v-else icon="fa-solid fa-check-circle" />
            <span>{{ isOtpLoading ? 'Đang xác thực...' : 'Xác thực OTP' }}</span>
          </button>
          
          <div class="text-center mt-3">
            <div v-if="otpCountdown > 0" class="text-muted fs-7 mb-2">
              Mã có hiệu lực trong: <span class="fw-bold text-primary-color">{{ formattedCountdown }}</span>
            </div>
            <div v-else class="mb-2">
              <span class="text-muted fs-7">Không nhận được mã? </span>
              <a href="javascript:void(0)" @click="handleResendOtp" class="text-primary-color fs-7 fw-bold text-decoration-none" :class="{ 'disabled': isResendingOtp }">
                <span v-if="isResendingOtp" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span v-else>Gửi lại mã OTP</span>
              </a>
            </div>
            <div class="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
              <a href="javascript:void(0)" @click="isOtpStep = false" class="text-secondary fs-7 text-decoration-none">
                <icon icon="fa-solid fa-arrow-left" class="me-1" /> Quay lại
              </a>
              <router-link to="/" class="text-primary-color fs-7 fw-bold text-decoration-none">
                <icon icon="fa-solid fa-house" class="me-1" /> Về trang chủ
              </router-link>
            </div>
          </div>
        </form>
      </div>
      </div>

      <!-- Mobile/Tablet Footer link -->
      <div class="mt-4 text-center">
        <span class="fs-7 text-muted">Chưa có tài khoản?&nbsp;</span>
        <router-link to="/register" class="fs-7 text-primary-color fw-bold text-decoration-none">
          Đăng ký ngay
        </router-link>
      </div>
    </div>
  </div>
</template>

<script src="./login.js"></script>
<style scoped src="./login.css"></style>
